import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "../components/site/Reveal";
import { QuoteForm } from "../components/site/QuoteForm";
import { ServiceGallery } from "../components/site/ServiceGallery";
import { SERVICES } from "../data/site";

const PageHeader = ({ eyebrow, title, subtitle }) => (
  <section className="relative overflow-hidden border-b border-white/10 pt-36 pb-16" data-testid="page-header">
    <div className="sfm-grid-bg pointer-events-none absolute inset-0 opacity-30" />
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber">{eyebrow}</p>
        <h1 className="mt-4 font-display text-4xl font-black uppercase leading-[1.03] tracking-tight text-white sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {subtitle && <p className="mt-5 max-w-2xl text-base text-white/60 sm:text-lg">{subtitle}</p>}
      </Reveal>
    </div>
  </section>
);

const Services = () => (
  <div>
    <PageHeader
      eyebrow="Our Services"
      title="Everything your storefront needs"
      subtitle="From the first concept sketch to the final installed sign, we handle design, production and construction in-house — seven core services, one accountable team."
    />

    <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
      <div className="space-y-20">
        {SERVICES.map((s, i) => (
          <Reveal key={s.id}>
            <div
              data-testid={`service-detail-${s.id}`}
              className={`grid items-center gap-8 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              {s.gallery ? (
                <ServiceGallery images={s.gallery} number={s.number} testId={s.id} />
              ) : (
                <figure className="relative m-0 overflow-hidden rounded-2xl border border-white/10">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.imageAlt || s.title}
                      loading="lazy"
                      decoding="async"
                      className={`h-full w-full object-cover ${s.imagePosition || "object-center"}`}
                    />
                  </div>
                  <span className="absolute left-5 top-5 font-mono text-sm uppercase tracking-[0.2em] text-amber">
                    {s.number}
                  </span>
                  {s.caption && (
                    <figcaption
                      data-testid={`service-caption-${s.id}`}
                      className="border-t border-white/10 bg-obsidian px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-white/55"
                    >
                      {s.caption}
                    </figcaption>
                  )}
                </figure>
              )}
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white sm:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-4 text-white/65">{s.tagline}</p>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/70">
                      <Check size={16} className="mt-0.5 shrink-0 text-amber" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex items-center gap-5">
                  <Link
                    to="/contact"
                    data-testid={`service-quote-${s.id}`}
                    className="group flex items-center gap-2 rounded-full bg-amber px-6 py-3 font-display text-sm font-bold uppercase tracking-wide text-void transition-transform hover:scale-[1.03] hover:bg-amber-glow"
                  >
                    Get a Quote
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <span className="font-mono text-[11px] uppercase tracking-widest text-white/40">
                    Typical turnaround · {s.turnaround}
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="border-t border-white/10 bg-obsidian py-20">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal className="mb-8 text-center">
          <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">
            Request a Quote
          </h2>
          <p className="mt-3 text-white/60">Pick a service and tell us what you have in mind.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <QuoteForm />
        </Reveal>
      </div>
    </section>
  </div>
);

export default Services;
